/**
 * `GET`/`POST /consola/suscripcion/plan` — el PLAN de suscripción mensual del conductor
 * (monto, intervalo, moneda), VERSIONADO igual que los parámetros de dispatch y la política de
 * precio. Antes era `SUSCRIPCION_MONTO_CENTIMOS`, una variable de entorno fija; esta pantalla lo
 * hace parametrizable desde la consola.
 *
 * 🔴 **Los tipos son PROPIOS, no los de `dominio/contratos.ts`** — mismo criterio que
 * `parametrosDispatchApi.ts` y `politicaPrecioApi.ts`: el backend puede devolver `string | null`
 * donde un tipo de mock declararía `string` obligatorio, y reusar el mock compilaría igual y
 * rompería en runtime al renderizar `null`.
 *
 * 🔑 **`'SIN_CONFIGURAR'` no es un caso de error, y NO tiene fallback seguro.** A diferencia de
 * dispatch (`origen: 'FALLBACK'` trae igual los seis números de arranque) y de precio
 * (`origen: 'FALLBACK'` trae el modelo por defecto que no compromete nada), acá **no existe un
 * monto de arranque razonable que inventar**: es dinero real que se le cobra a un conductor. Si
 * nadie publicó nunca un plan, `efectivos` es `{ origen: 'SIN_CONFIGURAR' }` y punto — sin
 * `id`/`version`/`montoCentimos` que leer. Es el estado esperable de una instalación recién
 * construida, no una falla de red; y mientras dure, ningún conductor puede iniciar su cobro
 * mensual (el backend responde 503 al alta de suscripción sin plan configurado).
 */
import { obtener, postear } from './clienteApi';

/** Cada cuánto se cobra el plan. Union de literales del backend — no hay bridge a `@taxi/contratos` para esto todavía. */
export type IntervaloSuscripcion = 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';

/**
 * Lo que gobierna el alta de suscripciones AHORA.
 *
 * Discriminada por `origen`, igual que `ParametrosEfectivos`/`PoliticaEfectiva` — pero acá el
 * lado `SIN_CONFIGURAR` no lleva NINGÚN otro campo. No hay `id`/`version`/`montoCentimos: null`
 * que copiar al formulario: el operador arranca de una pantalla en blanco, a propósito.
 */
export type PlanSuscripcionVigente =
  | {
      origen: 'BD';
      id: string;
      version: number;
      nombre: string;
      montoCentimos: number;
      intervalo: IntervaloSuscripcion;
      moneda: string;
    }
  | { origen: 'SIN_CONFIGURAR' };

/** Una fila del historial. Fechas serializadas como ISO string sobre HTTP (`Date` → string). */
export interface VersionPlanSuscripcion {
  id: string;
  version: number;
  vigenteDesde: string;
  /** `null` = es la versión VIGENTE. Es así como la pantalla la identifica. */
  vigenteHasta: string | null;
  nombre: string;
  montoCentimos: number;
  intervalo: IntervaloSuscripcion;
  moneda: string;
  /**
   * `null` cuando la versión no tiene autor registrado. **No se rellena con un centinela**: la
   * columna no distingue "nació con el sistema" de "alguien la escribió por fuera de la consola
   * y no quedó registrado", y ponerlo inventaría una atribución sobre una versión que decide
   * cuánto se le cobra a un conductor cada mes. Se muestra "sin registrar".
   */
  editadoPor: string | null;
  motivo: string | null;
}

export interface EstadoPlanSuscripcion {
  efectivos: PlanSuscripcionVigente;
  /** Historial completo, la más nueva primero. Vacío si nunca se publicó nada. */
  historial: VersionPlanSuscripcion[];
}

/**
 * El cuerpo del `POST`. `moneda`/`nombre` son opcionales del lado del backend (defaultean a
 * `'PEN'` y `'Apata — tarifa plana mensual'`) — pero el borrador de la pantalla SIEMPRE los
 * manda con un valor concreto, nunca `undefined`: quien publica tiene que ver en pantalla, y
 * poder editar, exactamente lo que va a quedar guardado.
 */
export interface PublicarPlanSuscripcion {
  /** Entero positivo — lo exige el backend. */
  montoCentimos: number;
  intervalo: IntervaloSuscripcion;
  moneda?: string;
  nombre?: string;
  /** Obligatorio, mínimo 10 caracteres — lo exige el backend (`@MinLength(10)`). */
  motivo: string;
}

export const obtenerPlanSuscripcion = (): Promise<EstadoPlanSuscripcion> =>
  obtener<EstadoPlanSuscripcion>('/consola/suscripcion/plan');

/**
 * Publica una versión NUEVA. Nunca edita la vigente.
 *
 * Puede fallar con **400** (monto inválido, motivo corto) o con **409** si otra publicación se
 * aplicó en simultáneo (`ErrorApi.status`): en ese caso hay que releer el estado antes de
 * reintentar, porque el borrador se armó sobre una versión que ya no es la vigente.
 */
export const publicarPlanSuscripcion = (
  cuerpo: PublicarPlanSuscripcion,
): Promise<VersionPlanSuscripcion> =>
  postear<VersionPlanSuscripcion>('/consola/suscripcion/plan', cuerpo);
